import { useCallback, useEffect } from 'react';

import { useSyncStorageObject } from 'hooks/use-local-storage-state/use-sync-storage-state';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { SyncStorageKey } from 'utils/environment/sync-storage';

import { addOffer, addReward } from './add-in-restaurant-cart-entry';
import { MIN_IN_RESTAURANT_CART_QUANTITY } from './constants';
import {
  ICart,
  ICartEntryType,
  ILegacyCartEntry,
  createCartEntry,
  existCartEntry,
  filterCartEntries,
  removeCartEntry,
  updateCartEntry,
} from './incentive-cart';
import { IAddInRestaurantCartEntryResult, IUseInRestaurantCart } from './types';
import { isCartEntryQuantityValid } from './utils';

/**
 * useInRestaurantCart holds the cart state and expose an API to interact with.
 */
export const useInRestaurantCart = (): IUseInRestaurantCart => {
  const enableLoyaltyStandardOffers = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS);
  const { isAuthenticated } = useAuthContext();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const [cart, updateCart, resetCart] = useSyncStorageObject<ICart>(
    SyncStorageKey.IN_RESTAURANT_REDEMPTION,
    []
  );

  const existEntryTypeInRestaurantCart: IUseInRestaurantCart['existEntryTypeInRestaurantCart'] = useCallback(
    type => existCartEntry(cart || [], (ce: ILegacyCartEntry) => ce.type === type),
    [cart]
  );

  // Cart Entry CRUD
  const addInRestaurantCartEntry: IUseInRestaurantCart['addInRestaurantCartEntry'] = item => {
    const newCartEntry = createCartEntry(item, {
      initialQuantity: MIN_IN_RESTAURANT_CART_QUANTITY,
    });
    let result: IAddInRestaurantCartEntryResult = {
      entry: null,
      evictedRewardEntries: [],
      decrementedRewardEntries: [],
    };

    updateCart(previousCart => {
      let cartResult = previousCart;
      let evictedRewardEntries: IAddInRestaurantCartEntryResult['evictedRewardEntries'] = [];
      let decrementedRewardEntries: IAddInRestaurantCartEntryResult['decrementedRewardEntries'] = [];

      switch (newCartEntry?.type) {
        case ICartEntryType.REWARD: {
          const { cart, evictedRewards, decrementedRewards } = addReward(
            previousCart,
            newCartEntry,
            enableRewardUnstacking
              ? { kind: 'capped', maxApplications: rewardRedemptionLimit }
              : { kind: 'unlimited' }
          );
          cartResult = cart;
          evictedRewardEntries = evictedRewards;
          decrementedRewardEntries = decrementedRewards;
          break;
        }
        case ICartEntryType.OFFER: {
          cartResult = addOffer(previousCart, newCartEntry);
          break;
        }
        default:
      }

      const isItemAdded = cartResult !== previousCart;
      result = {
        entry: isItemAdded ? newCartEntry : null,
        evictedRewardEntries: isItemAdded ? evictedRewardEntries : [],
        decrementedRewardEntries: isItemAdded ? decrementedRewardEntries : [],
      };

      return isItemAdded ? cartResult : previousCart;
    });

    return result;
  };

  const removeInRestaurantCartEntry: IUseInRestaurantCart['removeInRestaurantCartEntry'] = useCallback(
    cartEntry => {
      updateCart(prevCart => removeCartEntry(prevCart, cartEntry));
    },
    [updateCart]
  );

  const removeTypeFromCart = useCallback(
    (typeToRemove: ICartEntryType) => {
      updateCart(prevCart => filterCartEntries(prevCart, ce => ce.type !== typeToRemove));
    },
    [updateCart]
  );

  const clearInRestaurantCartAllRewards: IUseInRestaurantCart['clearInRestaurantCartAllRewards'] = useCallback(() => {
    removeTypeFromCart(ICartEntryType.REWARD);
  }, [removeTypeFromCart]);

  const updateInRestaurantCartEntryQuantity: IUseInRestaurantCart['updateInRestaurantCartEntryQuantity'] = useCallback(
    (cartEntry, quantity) => {
      if (cartEntry.type === ICartEntryType.REWARD && isCartEntryQuantityValid(quantity)) {
        updateCart(prevCart => updateCartEntry(prevCart, cartEntry, { quantity }));
      }
    },
    [updateCart]
  );

  /**
   * The hook propose is legacy compatibility, the only dependencies are the flag
   * `enableOffers3_0` and `isAuthenticated` because avoiding memoizing functions
   * the only received updates should be related to the flags.
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const offerTypeToRemove = enableLoyaltyStandardOffers
      ? ICartEntryType.LEGACY_OFFER
      : ICartEntryType.OFFER;

    if (existEntryTypeInRestaurantCart(offerTypeToRemove)) {
      removeTypeFromCart(offerTypeToRemove);
    }
  }, [
    enableLoyaltyStandardOffers,
    existEntryTypeInRestaurantCart,
    isAuthenticated,
    removeTypeFromCart,
  ]);

  return {
    inRestaurantCart: cart || [],
    addInRestaurantCartEntry,
    removeInRestaurantCartEntry,
    updateInRestaurantCartEntryQuantity,
    resetInRestaurantCart: resetCart,
    existEntryTypeInRestaurantCart,
    clearInRestaurantCartAllRewards,
    isInRestaurantCartEmpty: !cart?.length,
    removeTypeFromCart,
  };
};
